import { skipToken, useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import type { PublishedContextValue } from '@/components/views/published/PublishedContext';
import { usePublishedContext } from '@/components/views/published/PublishedContext';
import { useOrgMemberByIdQuery } from '@/lib/queries/user-management/useOrgMemberByIdQuery';
import { userManagementQueryKeys } from '@/lib/query-keys/userManagement';

export interface OrgMemberDisplay {
  displayLabel: string;
  pictureUrl: string | null;
}

function publishedToDisplay(
  ctx: PublishedContextValue,
  userId: string,
): OrgMemberDisplay | undefined {
  const panelItemId = ctx.panelItemId;
  const panelData = panelItemId !== undefined ? ctx.getItemPanelData(panelItemId) : undefined;
  const resolvedUser = panelData?.resolvedUsers?.[userId];
  if (!resolvedUser) return undefined;
  return { displayLabel: resolvedUser.name, pictureUrl: resolvedUser.pictureUrl };
}

export const useOrgMemberByIdQueryWithPublished = (userId: string) => {
  const publishedContext = usePublishedContext();
  const isPublished = publishedContext !== null;

  const memberQuery = useOrgMemberByIdQuery(userId, !isPublished);

  const publishedQuery = useQuery<OrgMemberDisplay | undefined>({
    queryKey: [...userManagementQueryKeys.userManagement.profile(userId), 'published'],
    queryFn: skipToken,
    initialData: () =>
      publishedContext && userId ? publishedToDisplay(publishedContext, userId) : undefined,
    enabled: false,
    staleTime: Infinity,
    gcTime: Infinity,
  });

  const display = useMemo<OrgMemberDisplay | undefined>(() => {
    const user = memberQuery.data;
    if (!user) return undefined;
    return {
      displayLabel: user.name ?? user.email,
      pictureUrl: user.pictureUrl ?? null,
    };
  }, [memberQuery.data]);

  if (isPublished) return publishedQuery;
  return { ...memberQuery, data: display };
};
